import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ApiResponse, OrderPayment, CartItem } from '../interfaces';
import { Observable } from 'rxjs';

import { AuthService } from './auth.service';
import { EntityService } from './entity.service';
import { Constants } from '../configs';
import { UpdateShippingOP } from './../interfaces';
import { Address } from '../models/places';
import { ConfigurationPrint } from '../../sale/interface';
import { measurableProduct } from '../configs/measurable-product.config';
import { transformAccordingToDecimals } from '../helpers/util';

@Injectable({
  providedIn: 'root'
})
export class OrderPaymentService {
  documentTypes = Constants.Customer.DOCUMENT_TYPES.getProps();
  paymentMethods = Constants.ConfigQuotation.PAYMENT_TYPES_ENTERPRISE.getProps();
  typeSend = Constants.SaleConfig;
  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private entityService: EntityService
  ) { }

  setOrderPayment(body: OrderPayment): Observable<ApiResponse> {
    const items: CartItem[] = body.details.reduce(this.removeDuplicates, []);
    const details = JSON.parse(JSON.stringify(this.generateOpDetails(items)));
    body.details = details;
    return this.apiService
      .put(`${(this.apiService.orderPayment)}/order-payment`, body)
      .pipe();
  }

  /**
   * Reduce function to perform a duplicate removal
   * @param acc
   * @param curr
   * @param index
   * @param arr
   */
  removeDuplicates(acc: CartItem[], curr: CartItem) {
    /**
     * Scope free compare function, the criteria to compare equal conditions is
     * origin + deliveryMode + deliveryDate + dispatchZone + destination(address + number)
     * @param item
     * @param value
     */
    const equalCartItem = (cartItem: CartItem, value: CartItem) => {
      const configCompany = cartItem.origin ? Constants.Company.COMPANY_TYPES.find(i => cartItem.origin.includes(i.code)) : null;
      const applySkuDuplicate = configCompany ? configCompany.applySkuDuplicate : false;
      const aDeliveryAddress = (cartItem.deliveryAddress && cartItem.deliveryMode.includes('HOME_DELIVERY') && cartItem.deliveryAddress.address)
        ? cartItem.deliveryAddress.address + cartItem.deliveryAddress.number : '';
      const bDeliveryAddress = (value.deliveryAddress && value.deliveryMode.includes('HOME_DELIVERY') && value.deliveryAddress.address)
        ? value.deliveryAddress.address + value.deliveryAddress.number : '';
      const aDestination = (cartItem.destination && cartItem.deliveryMode.includes('STORE_WITHDRAWAL'))
        ? cartItem.destination : '';
      const bDestination = (value.destination && value.deliveryMode.includes('STORE_WITHDRAWAL'))
        ? value.destination : '';
      return !applySkuDuplicate
        && value.skuCode.toUpperCase() === cartItem.skuCode.toUpperCase()
        && value.deliveryMode.toUpperCase() === cartItem.deliveryMode.toUpperCase()
        && (value.origin || '').toUpperCase() === (cartItem.origin || '').toUpperCase()
        && value.deliveryDate.toUpperCase() === cartItem.deliveryDate.toUpperCase()
        && aDestination.toUpperCase() === bDestination.toUpperCase()
        && (value.dispatchZone || '').toUpperCase() === (cartItem.dispatchZone || '').toUpperCase()
        && aDeliveryAddress.toUpperCase() === bDeliveryAddress.toUpperCase();
    };
    /**
     * ***
     * @param cartItem
     * @param item
     */
    const getValidateDiscount = (cartItem: CartItem, item: CartItem): number  => {
      let discountManual = 0;
      switch (cartItem.discountType) {
        case 'PERCENTAGE_AMOUNT':
          const dItemSoles = (item.price * item.additionalDiscount / 100) * item.quantity;
          const acumCartItemSoles = (cartItem.price * cartItem.additionalDiscount / 100) * cartItem.quantity;
          const sum = (acumCartItemSoles + dItemSoles) / (Number(item.quantity) + Number(cartItem.quantity));
          discountManual = (100 * sum) / (cartItem.price);
          break;
        case 'NUMERIC_AMOUNT':
        case 'DISCOUNT_UNIT':
          const cartItemSoles = cartItem.additionalDiscount * cartItem.quantity;
          const itemSoles = item.additionalDiscount * item.quantity
          discountManual = (cartItemSoles + itemSoles) / (Number(cartItem.quantity) + Number(item.quantity));
          break;
      }
      return Number(discountManual.toFixed(2));
    }

    const existent: CartItem = acc.find((value: CartItem) => {
      const exists = equalCartItem(value, curr);
      return exists;
    });

    const indexOfList = acc.findIndex((value: CartItem) => {
      const exists = equalCartItem(value, curr);
      return exists;
    });
    if (existent === undefined) {
      acc.push(curr);
    } else {
      const cartItem = existent;
      if (cartItem.discount || curr.discount) {
        const discount = cartItem.discount + curr.discount;
        cartItem.discount = discount;
      }
      if (cartItem.additionalDiscount  || curr.additionalDiscount) {
        cartItem.additionalDiscount = getValidateDiscount(cartItem, curr);
      }
      let quantity: number = Number(cartItem.quantity) + Number(curr.quantity);
      if (curr.measurable === measurableProduct.isMeasurable && curr.conversionFactor) {
        const numberOfDecimals = curr.conversionFactor.toString().length - 1;
        quantity = transformAccordingToDecimals(Number(quantity), numberOfDecimals);
      } else {
        quantity = parseInt(quantity.toString(), 10);
      }
      cartItem.quantity = Number(quantity);
      if (cartItem.services) {
        cartItem.services = cartItem.services.concat(curr.services);
      }
      acc[indexOfList] = cartItem;
    }
    return acc;
  }

  generateOpCustomer(customer): any {
    return {
      name: customer.name,
      lastName: customer.lastName,
      documentType: customer.documentType,
      documentNumber: customer.identityDocument,
      contactEmail: customer.email,
      telephoneContact: customer.phone,
      withdrawPerson: customer.nameAditional,
      withdrawPersonDocument: customer.documentAditional,
      companySalesEnabled: customer.companySalesEnabled
    };
  }

  generateOpDetails(cart: CartItem[]): CartItem[] {
    const newCart = JSON.parse(
      JSON.stringify(cart)
    );
    return newCart.map((cartItem: CartItem) => {
      if (cartItem.deliveryMode === this.typeSend.HOME_DELIVERY) {
        delete cartItem.destination;
        delete cartItem.destinationDescription;
        delete cartItem.destinationType;
      } else {
        delete cartItem.shippingAmount;
        delete cartItem.deliveryAddress;
      }
      delete cartItem.totalPrice;
      delete cartItem.dscto;
      delete cartItem.data;
      delete cartItem.finalPrice;
      delete cartItem.servicesAddress;
      delete cartItem.dispatch;
      delete cartItem.available;
      delete cartItem.zoneDefault;
      delete cartItem.provinceDefault;
      delete cartItem.observation;
      delete cartItem.destinationZone;
      delete cartItem.error;
      delete cartItem.authorize;
      delete cartItem.priceLine;
      return cartItem;
    });
  }

  getPhone(data) {
    const phone = data.filter(item => item.contactType === 'PHONE');
    return phone[phone.length - 1].contact;
  }

  resetAddress(address: Address): Address {
    if (!address) {
      return null;
    }

    return {
      department: address.department ? address.department : address.ubigeo.department,
      province: address.province ? address.province : address.ubigeo.province || '-',
      ubigeoCode: address.ubigeoCode,
      wayType: address.wayType,
      departmentNumber: address.departmentNumber,
      floor: address.floor,
      addressType: address.addressType,
      address: address.address,
      reference: address.reference,
      number: address.number,
      latitude: address.latitude,
      longitude: address.longitude,
      zoneId: address.zoneId
    };
  }

  getUbigeoStore() {
    return this.entityService
      .getLocalStores()
      .find(
        store =>
          store.entityCode === this.authService.getUser().entity.entityCode
      );
  }

  updateShipping(cart, availableDates): UpdateShippingOP {
    let dateUpdated = false;
    const newCart = cart.map((cartItem, i) => {
      cartItem.data.amountShipping = availableDates[i].amountShipping;
      cartItem.data.availableDates = availableDates[0].dateList;
      if (cartItem.data.availableDates.indexOf(cartItem.orderDate) === -1) {
        cartItem.orderDate = cartItem.data.availableDates[0];
        dateUpdated = true;
      }
      return cartItem;
    });
    return { newCart, dateUpdated };
  }

  shippingAuthorization(discounts: any, orderPayment: OrderPayment): OrderPayment {
    if (discounts.action === 'updateShipping') {
      orderPayment.payInfo.shippingCost = Number(discounts.shipping);
    } else {
      orderPayment.payInfo.shippingCost = Number(0.01);
    }
    return orderPayment;
  }

  getServiceAditional(orderPayment: OrderPayment): OrderPayment {
    const opService = JSON.parse(JSON.stringify(orderPayment));
    const cartService: CartItem[] = JSON.parse(JSON.stringify(opService.details));
    opService.details = cartService.map(cartItem => {
      cartItem.services = cartItem.services.reduce((acc, curr) => {
        if (curr.service.skuCode !== '') {
            curr.service.quantity = cartItem.quantity;
          if (curr.type ==='ASSEMBLY') {
            curr.service.address = cartItem.deliveryMode === this.typeSend.HOME_DELIVERY && cartItem.deliveryAddress ? cartItem.deliveryAddress : cartItem.servicesAddress;
          }
          acc.push(curr);
        }
        return acc;
      }, []);
      return cartItem;
    });
    return opService;
  }

  confirmQuotation(orderPayment: OrderPayment, op: any): ConfigurationPrint {
    const user = this.authService.getUser();
    return {
      email: orderPayment.customer.contactEmail,
      phone: orderPayment.customer.telephoneContact,
      title: 'Orden de pago emitida',
      company: user.entity.company.code,
      op: op.orderNumber,
      documentType: this.documentTypes.find(
        documentType =>
          documentType.value === orderPayment.customer.documentType
      ).text.toUpperCase(),
      documentNumber: orderPayment.customer.documentNumber,
      name: orderPayment.customer.name + ' ' + orderPayment.customer.lastName,
      paymentMethod: orderPayment.payInfo.paymentMethod ? this.paymentMethods.find(
        paymentMethod =>
          paymentMethod.value === orderPayment.payInfo.paymentMethod
      )?.viewValue : undefined,
      code:
        op.orderNumber,
      saleEntity: orderPayment.payInfo.saleEntity,
      endEffectiveDate: op.endEffectiveDate,
      service: 'orderPaymentService',
      message: 'Orden de pago emitido'
    };
  }

  print(op, companyCode): Observable<any> {
    return this.apiService
      .getHTML(`${this.apiService.orderPayment}/order-payment/${op}/${companyCode}/invoice`)
      .pipe();
  }

  printThermal(op, companyCode): Observable<any> {
    return this.apiService
      .getHTML(`${this.apiService.orderPayment}/order-payment/${op}/${companyCode}/invoice/thermal`)
      .pipe();
  }

  validateCustomer(customer, state): string[] {
    const list = [];
    if (!customer) {
      list.push('Ingrese datos del cliente');
    } else if (!state) {
      list.push('Completar los campos obligatorios del cliente');
    }
    return list;
  }

  validateCart(cart: CartItem[], listError: any): string[] {
    const error = [];
    if (cart.length) {
      cart.reduce((list, currentCart, index) => {
        const itemsErr = JSON.parse(JSON.stringify(listError));
        const services = currentCart.services[0] && currentCart.services[0].service.skuCode !== '';
        if (currentCart.deliveryMode !== 'HOME_DELIVERY') {
          delete itemsErr.deliveryAddress;
          delete itemsErr.shippingAmount;
        }
        if (currentCart.productType !== this.typeSend.productType.ST) {
          delete itemsErr.shippingAmount;
        }
        if (services) {
          if (currentCart.servicesAddress) {
            delete itemsErr.servicesAddress;
          }
        } else {
          delete itemsErr.servicesAddress;
        }
        const err = Object.keys(itemsErr).filter(prop => !currentCart[prop]).map(prop => itemsErr[prop]).join(' / ');
        if (err) {
          list.push(`${index + 1}: ${err}`);
        }
        return list;
      }, error);
    } else {
      error.push('Registre un producto y/o servicio');
    }
    return error;
  }
}
